import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '../../service/auth.service';
import { LocalStorageService } from 'ngx-webstorage';
import { NotificationHandler } from '../../helper/notification.handler';
import { UrlHelper } from '../../helper/url.helper';
import { LocalStorageKeys } from '../../helper/local-storage.keys';
import { User } from '../../model/auth/User';
import { UserService } from '../../service/user.service';
import { TenantService } from '../../service/tenant.service';

@Component({
  selector: 'sign-up-confirm',
  templateUrl: './sign-up-confirm.component.html',
  styleUrls: ['./sign-up-confirm.component.scss']
})
export class SignUpConfirmComponent implements OnInit {

  inProgress = false;
  email: string | null;
  username: string | null;

  user?: User | null;

  returnUrl?: string;

  emailField = new FormControl({value: '', disabled: true}, [
    Validators.email, Validators.required
  ]);
  codeField = new FormControl('', [
    Validators.required, Validators.pattern('[0-9]*'), Validators.minLength(6), Validators.maxLength(6)
  ]);

  signUpConfirmForm: FormGroup = new FormGroup({
    email: this.emailField,
    code: this.codeField,
  });

  constructor(private route: ActivatedRoute,
              private router: Router,
              private authService: AuthService,
              private userService: UserService,
              private tenantService: TenantService,
              private localStorageService: LocalStorageService) {
    this.username = this.route.snapshot.paramMap.get('username');
    this.email = this.route.snapshot.paramMap.get('email');
    this.emailField.setValue(this.email);

    this.returnUrl = decodeURI(this.route.snapshot.queryParams.returnUrl as string || '');
  }

  async ngOnInit() {
    this.user = await this.authService.getUser();

    const competition = this.tenantService.getTenant();
    if (competition) {
      this.authService.updateTenantConfig(competition.name, competition.domain);
    }
  }

  shouldDisableSubmit() {
    return !this.emailField.value || !this.codeField.valid;
  }

  async sendAgain() {
    if (!this.username) {
      NotificationHandler.handleError($localize`:Sign Up Confirm|User email is missing error message@@sign-up-user-email-missing-error:Email address is missing`);
      return;
    }

    try {
      if (!this.user) {
        await this.authService.resendSignUp(this.username);

        NotificationHandler.notification($localize`:Sign Up Confirm|Code re-send confirmation message@@sign-up-code-re-send-confirmation-message:A code has been emailed to you`);
      } else if (!this.user.emailVerified) {
        await this.authService.verifyCurrentUserAttribute('email');

        NotificationHandler.notification($localize`:Sign Up Confirm|Code re-send confirmation message@@sign-up-code-re-send-confirmation-message:A code has been emailed to you`);
      }
    } catch (e) {
      const code: string = (e as { [key: string]: string | undefined })['code'] as string;

      if (code == 'LimitExceededException') {
        NotificationHandler.handleWarning($localize`:Sign Up Confirm|Code re-send failed limit error message@@sign-up-code-re-send-failed-limit-error:Attempt limit exceeded, please try after some time`, e, false);
      } else {
        NotificationHandler.handleError($localize`:Sign Up Confirm|Code re-send failed error message@@sign-up-code-re-send-failed-error:Confirmation code re-send has failed`, e);
      }
    }
  }

  async confirmCode() {
    if (!this.email) {
      NotificationHandler.handleError($localize`:Sign Up Confirm|User email is missing error message@@sign-up-user-email-missing-error:Email address is missing`);
      return;
    }
    if (!this.username) {
      NotificationHandler.handleError($localize`:Sign Up Confirm|Username is missing error message@@sign-up-username-missing-error:Username is missing`);
      return;
    }

    if (this.signUpConfirmForm.valid) {
      this.inProgress = true;

      try {
        const navigationExtras = this.returnUrl
          ? {
            queryParams: {
              returnUrl: encodeURI(this.returnUrl)
            }
          }
          : {};

        if (!this.user) {
          await this.authService.confirmSignUp(this.username, this.codeField.value as string);

          const email = this.localStorageService.retrieve(LocalStorageKeys.SIGN_UP_CONFIRM_EMAIL) as string;
          const pass = this.localStorageService.retrieve(LocalStorageKeys.SIGN_UP_CONFIRM_PASSWORD) as string;

          if (email && pass) {
            try {
              await this.authService.signIn(this.email, pass);
              await this.router.navigate([UrlHelper.ROLE_SELECT_PATH], navigationExtras);
            } catch (e) {
              NotificationHandler.handleError(e);
              await this.router.navigate([UrlHelper.SIGN_IN_PATH], {replaceUrl: true});
            } finally {
              this.localStorageService.clear(LocalStorageKeys.SIGN_UP_CONFIRM_EMAIL);
              this.localStorageService.clear(LocalStorageKeys.SIGN_UP_CONFIRM_PASSWORD);
            }
          } else {
            NotificationHandler.handleError($localize`:Sign Up Confirm|Sign up confirm failed error message@@sign-up-confirm-failed-error:Something went wrong while sign up`);
            await this.router.navigate([UrlHelper.SIGN_IN_PATH], {replaceUrl: true});
          }
        } else if (!this.user.emailVerified) {
          try {
            await this.authService.verifyCurrentUserAttributeConfirm('email', this.codeField.value as string);

            this.userService.reportUpdateUser(this.user.id).subscribe(() => {
            }, error => {
              NotificationHandler.handleError($localize`:User Profile|User profile update report failed notification@@user-profile-report-failed-notification:Profile update report failed`, error);
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
            }, async () => {
              await this.router.navigate([UrlHelper.ROLE_SELECT_PATH], navigationExtras);
            });
          } catch (err) {
            const code: string = (err as { [key: string]: string | undefined })['code'] as string;

            if (code == 'CodeMismatchException') {
              this.inProgress = false;
              NotificationHandler.handleWarning($localize`:User Profile|Email confirm invalid code error@@user-profile-email-confirm-invalid-code-error:Invalid verification code provided, please try again`);
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            } else if (code == 'ExpiredCodeException') {
              this.inProgress = false;
              NotificationHandler.handleWarning($localize`:User Profile|Email confirm expired code error@@user-profile-email-confirm-expired-code-error:Expired code provided, please request a code again`);
            } else {
              NotificationHandler.handleError(err);
              await this.router.navigate([UrlHelper.SIGN_IN_PATH], {replaceUrl: true});
            }
          }
        }
      } catch (error) {
        const code: string = (error as { [key: string]: string | undefined })['code'] as string;

        if (code == 'CodeMismatchException') {
          this.inProgress = false;
          NotificationHandler.handleWarning($localize`:User Profile|Email confirm invalid code error@@user-profile-email-confirm-invalid-code-error:Invalid verification code provided, please try again`);
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        } else if (code == 'ExpiredCodeException') {
          this.inProgress = false;
          NotificationHandler.handleWarning($localize`:User Profile|Email confirm expired code error@@user-profile-email-confirm-expired-code-error:Expired code provided, please request a code again`);
        } else {
          NotificationHandler.handleError(error);
        }

        this.inProgress = false;
      }
    }
  }
}
